import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { API_URL, ApiError, hillsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { ExternalProjectSummary, HillChart } from '@hillchart/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import type { FormEvent } from 'react';
import { useId, useMemo, useState } from 'react';

interface ImportHillDialogProps {
  /** Called with the created hill after a successful import. */
  onImported?: (hill: HillChart) => void;
}

/**
 * Dialog that imports a GitLab project as a new hill chart, in two screens:
 * pick a project, then name the pitch.
 *
 * The naming screen is not cosmetic - it is the only moment `title` is
 * written at creation. Selecting a project deliberately creates nothing, so a
 * hill never exists under a transitional name copied from the repo.
 *
 * Which screen is showing is derived from `selected` rather than held in a
 * separate `step` state: two states that must agree eventually disagree, and
 * deriving makes "naming screen with no project" unrepresentable.
 *
 * UX rationale on the picker: we hide already-imported projects rather than
 * greying them out. The backend enforces the one-to-one invariant (`create`
 * is idempotent), but showing duplicates in the combobox just confuses the
 * user - the "create" action on a duplicate would return the existing hill
 * and feel like a no-op. `alreadyImported` is pre-computed server-side so we
 * don't need a second round-trip.
 *
 * The GitLab projects query only fires when the dialog opens (`enabled: open`)
 * to avoid hitting the GitLab API on every page mount.
 */
export function ImportHillDialog({ onImported }: ImportHillDialogProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ExternalProjectSummary | null>(null);
  const [title, setTitle] = useState('');
  const titleInputId = useId();
  const errorId = useId();
  const qc = useQueryClient();

  const projectsQuery = useQuery({
    queryKey: queryKeys.gitlabProjects(),
    queryFn: () => hillsApi.gitlabProjects(),
    enabled: open,
    staleTime: 60_000,
  });

  // Single source of truth for "the dialog transitioned open/closed". Radix
  // calls `onOpenChange` for its own dismissals (ESC, overlay click), but
  // NOT when we close the dialog ourselves via a parent `setOpen(false)` -
  // that would be a loop. So every programmatic close (Cancel button,
  // successful mutation) must also route through here to stay consistent,
  // otherwise the search input keeps its stale value across reopens and
  // hides all projects behind a filter the user can't see anymore.
  //
  // Three values reset here now, not one: a stale selection would reopen the
  // dialog straight onto the naming screen of a project the user has since
  // forgotten about.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch('');
      setSelected(null);
      setTitle('');
    }
  };

  const importMutation = useMutation({
    mutationFn: (input: { projectId: string; title: string }) =>
      hillsApi.create({ externalProjectId: input.projectId, title: input.title }),
    onSuccess: (hill) => {
      qc.invalidateQueries({ queryKey: queryKeys.hillsList() });
      qc.invalidateQueries({ queryKey: queryKeys.gitlabProjects() });
      onImported?.(hill);
      handleOpenChange(false);
    },
  });

  const importableProjects = useMemo<ExternalProjectSummary[]>(() => {
    const all = projectsQuery.data?.projects ?? [];
    return all.filter((p) => !p.alreadyImported);
  }, [projectsQuery.data]);

  // Selecting a project advances to the naming screen. Nothing is created and
  // nothing leaves the browser until the user submits a title.
  //
  // The mutation is reset on the way in, because its error state outlives the
  // screen that produced it: a failed import on project A would otherwise
  // still be rendering "Import failed: ..." when the user lands on project B's
  // naming screen, blaming B for something A did. Resetting here rather than
  // in `handleOpenChange` covers both routes back to this screen (Back, and
  // close-then-reopen) without `onSuccess` resetting the mutation from inside
  // its own callback.
  const handleSelect = (project: ExternalProjectSummary) => {
    importMutation.reset();
    setSelected(project);
  };

  // Back clears the draft title as well as the selection: a title typed for
  // one project must not silently become the name of the next one.
  const handleBack = () => {
    setSelected(null);
    setTitle('');
  };

  const trimmedTitle = title.trim();
  const canSubmit = trimmedTitle.length > 0 && !importMutation.isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selected === null || !canSubmit) return;
    importMutation.mutate({ projectId: selected.id, title: trimmedTitle });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4" />
          Import project
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          {/* The title stays put across both screens. Radix requires one at all
              times, and the dialog's identity has not changed - it is the same
              import flow, one step further in. The description is the part that
              swaps: the picker's filtering note means nothing once a project is
              picked. */}
          <DialogTitle>Import a GitLab project</DialogTitle>
          <DialogDescription>
            {selected === null
              ? "Pick a project to turn its milestones into a hill chart. Projects already imported don't appear here."
              : 'Name the pitch this hill chart tracks. You can rename it later.'}
          </DialogDescription>
        </DialogHeader>

        {selected === null ? (
          <>
            <Command shouldFilter={true}>
              <CommandInput
                placeholder="Search projects..."
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                {projectsQuery.isLoading ? (
                  <div className="py-6 text-center text-slate-500 text-sm">Loading projects...</div>
                ) : projectsQuery.isError ? (
                  <div className="flex flex-col items-center gap-3 py-6">
                    <p className="text-center text-red-600 text-sm">
                      {projectsQuery.error instanceof ApiError &&
                      projectsQuery.error.code === 'GITLAB_TOKEN_EXPIRED'
                        ? 'Your GitLab session has expired.'
                        : "Couldn't load GitLab projects."}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => projectsQuery.refetch()}
                        disabled={projectsQuery.isFetching}
                      >
                        {projectsQuery.isFetching ? 'Retrying…' : 'Try again'}
                      </Button>
                      <Button size="sm" asChild>
                        <a href={`${API_URL}/auth/gitlab/login`}>Re-authenticate with GitLab</a>
                      </Button>
                    </div>
                  </div>
                ) : importableProjects.length === 0 ? (
                  <CommandEmpty>
                    {projectsQuery.data?.projects.length
                      ? 'Every project is already imported.'
                      : 'No projects found in GitLab.'}
                  </CommandEmpty>
                ) : (
                  <CommandGroup heading="Projects">
                    {importableProjects.map((project) => (
                      <CommandItem
                        key={project.id}
                        value={`${project.name} ${project.id}`}
                        onSelect={() => handleSelect(project)}
                      >
                        <div className="flex flex-1 flex-col">
                          <span className="font-medium">{project.name}</span>
                          <span className="text-slate-500 text-xs">
                            {project.milestoneCount} milestone
                            {project.milestoneCount === 1 ? '' : 's'}
                            {project.state ? ` · ${project.state}` : ''}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* The backing project, repeated so the user names the pitch
                knowing what it tracks. The milestone count is the one fetched
                when the dialog opened; it is context for naming, not the data
                the hill is built from - the handler re-fetches milestones. */}
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="font-medium text-sm">{selected.name}</p>
              <p className="text-slate-500 text-xs">
                {selected.milestoneCount} milestone{selected.milestoneCount === 1 ? '' : 's'}
                {selected.state ? ` · ${selected.state}` : ''}
              </p>
            </div>

            <div className="space-y-1">
              {/* A visible label rather than the `aria-label` `<HillTitle />`
                  uses: there the input replaces an existing heading and has no
                  room for one, here the dialog is empty and the label can just
                  be read. */}
              <label htmlFor={titleInputId} className="block font-medium text-sm">
                Pitch title
              </label>
              <input
                // biome-ignore lint/a11y/noAutofocus: the field is the only reason this screen exists; focus has to land on it.
                autoFocus
                id={titleInputId}
                value={title}
                maxLength={255}
                placeholder="What is this cycle about?"
                // The failure is reported on the field, not just next to it:
                // focus stays in the input after a failed submit, so without
                // these a screen-reader user gets no signal at all.
                aria-invalid={importMutation.isError}
                aria-describedby={importMutation.isError ? errorId : undefined}
                onChange={(e) => setTitle(e.target.value)}
                // `readOnly` rather than `disabled` while the create is in
                // flight, for the same reason as `<HillTitle />`: a disabled
                // input drops focus and stops being announced.
                readOnly={importMutation.isPending}
                className="w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-900/20"
              />
            </div>

            {importMutation.isError ? (
              <p role="alert" id={errorId} className="text-red-600 text-sm">
                Import failed: {importMutation.error.message}
              </p>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={importMutation.isPending}
              >
                Back
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {importMutation.isPending ? 'Creating…' : 'Create hill'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
